import { useState, useCallback, useMemo } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { create } from '@bufbuild/protobuf'
import { timestampFromDate } from '@bufbuild/protobuf/wkt'
import { Skeleton } from 'skullet/react'
import {
  ListChecks,
  Circle,
  CheckCircle2,
  Loader2,
  ChevronRight,
  Inbox,
  Calendar,
  AlertTriangle,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatShortDate, timestampToDateOnly } from '@/lib/date-utils'
import { useCurrentUser } from '@/stores/user.store'
import { useTasksList, useUpdateTask } from '@/stores/tasks.store'
import { TaskStatusEnum, ParentTypeEnum } from '@/gen/cortado/v1/settings_pb'
import { TaskLiteSchema, type TaskLite } from '@/gen/cortado/v1/actions_pb'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'

type PopoverContentProps = React.ComponentProps<typeof PopoverContent>
type PopoverSide = PopoverContentProps['side']
type PopoverAlign = NonNullable<PopoverContentProps['align']>

// ─── Types ───────────────────────────────────────────────────────────────────

type DueGroup = {
  label: string
  items: TaskLite[]
}

type TabFilter = 'all' | 'today'

// ─── Due date helpers ────────────────────────────────────────────────────────

function getStartOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate())
}

function getDueStatus(task: TaskLite): 'overdue' | 'today' | 'future' | 'none' {
  // Due date is date-only (UTC-midnight on the wire); read the UTC calendar day
  // so comparisons happen on the day the user picked, not the prior day.
  const dueOnly = timestampToDateOnly(task.dueDate)
  if (!dueOnly) return 'none'
  const now = getStartOfDay(new Date())
  const due = getStartOfDay(dueOnly)
  if (due < now) return 'overdue'
  if (due.getTime() === now.getTime()) return 'today'
  return 'future'
}

function getParentRoute(parentType: ParentTypeEnum, parentId: string): { route: string; label: string } | null {
  switch (parentType) {
    case ParentTypeEnum.PARENT_TYPE_INQUIRY:
      return { route: `/leads/${parentId}`, label: 'Lead' }
    case ParentTypeEnum.PARENT_TYPE_EVENT:
      return { route: `/events/${parentId}`, label: 'Event' }
    case ParentTypeEnum.PARENT_TYPE_BOOKING:
      return { route: `/events/${parentId}`, label: 'Booking' }
    case ParentTypeEnum.PARENT_TYPE_CONTACT:
      return { route: `/crm/contacts/${parentId}`, label: 'Contact' }
    case ParentTypeEnum.PARENT_TYPE_INVOICE:
      return { route: `/invoices/${parentId}`, label: 'Invoice' }
    default:
      return null
  }
}

function groupByDue(items: TaskLite[]): DueGroup[] {
  if (items.length === 0) return []

  const groups: Record<string, TaskLite[]> = {}
  const order = ['Overdue', 'Today', 'Upcoming', 'No due date']

  for (const item of items) {
    const status = getDueStatus(item)
    let label: string
    if (status === 'overdue') label = 'Overdue'
    else if (status === 'today') label = 'Today'
    else if (status === 'future') label = 'Upcoming'
    else label = 'No due date'

    if (!groups[label]) groups[label] = []
    groups[label].push(item)
  }

  return order.filter((l) => groups[l]?.length).map((label) => ({ label, items: groups[label] }))
}

// ─── Due date styling ────────────────────────────────────────────────────────

const dueStyles = {
  overdue: {
    bg: 'bg-red-500/10',
    text: 'text-red-600 dark:text-red-400',
    border: 'border-l-red-500',
    badge: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
    groupText: 'text-red-500/80',
  },
  today: {
    bg: 'bg-amber-500/10',
    text: 'text-amber-600 dark:text-amber-400',
    border: 'border-l-amber-500',
    badge: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
    groupText: 'text-amber-500/80',
  },
  future: {
    bg: '',
    text: 'text-muted-foreground',
    border: 'border-l-transparent',
    badge: 'bg-muted text-muted-foreground',
    groupText: 'text-muted-foreground/60',
  },
  none: {
    bg: '',
    text: 'text-muted-foreground',
    border: 'border-l-transparent',
    badge: 'bg-muted text-muted-foreground',
    groupText: 'text-muted-foreground/60',
  },
} as const

const groupLabelStyles: Record<string, string> = {
  'Overdue': dueStyles.overdue.groupText,
  'Today': dueStyles.today.groupText,
  'Upcoming': dueStyles.future.groupText,
  'No due date': dueStyles.none.groupText,
}

const SAMPLE_DUE_GROUPS: DueGroup[] = [
  {
    label: 'Upcoming',
    items: ['Confirm final headcount', 'Send tasting menu to client', 'Book rental delivery'].map((title, i) =>
      create(TaskLiteSchema, {
        id: `sample-${i}`,
        title,
        taskStatus: TaskStatusEnum.TASK_STATUS_NOT_STARTED,
        dueDate: timestampFromDate(new Date(Date.UTC(2026, 0, i + 1))),
        parentType: ParentTypeEnum.PARENT_TYPE_EVENT,
        parentId: `sample-event-${i}`,
        parentName: 'Hart wedding',
      }),
    ),
  },
]

// ─── Entry point ─────────────────────────────────────────────────────────────

interface TaskFeedProps {
  className?: string
  /** Where the feed panel opens relative to its trigger. */
  side?: PopoverSide
  align?: PopoverAlign
}

export function TaskFeed({ className, side, align = 'end' }: TaskFeedProps) {
  const { user } = useCurrentUser()
  const [isOpen, setIsOpen] = useState(false)

  if (!user?.id) {
    return (
      <Button variant="ghost" size="icon" className={className} disabled>
        <ListChecks className="h-5 w-5 text-muted-foreground" />
        <span className="sr-only">Tasks</span>
      </Button>
    )
  }

  return (
    <TaskFeedInner
      className={className}
      side={side}
      align={align}
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      userId={user.id}
    />
  )
}

// ─── Main feed ───────────────────────────────────────────────────────────────

interface TaskFeedInnerProps {
  className?: string
  side?: PopoverSide
  align: PopoverAlign
  isOpen: boolean
  setIsOpen: (open: boolean) => void
  userId: string
}

function TaskFeedInner({ className, side, align, isOpen, setIsOpen, userId }: TaskFeedInnerProps) {
  const navigate = useNavigate()
  const [tab, setTab] = useState<TabFilter>('all')

  const activeStatuses = useMemo(
    () => [TaskStatusEnum.TASK_STATUS_NOT_STARTED, TaskStatusEnum.TASK_STATUS_IN_PROGRESS],
    [],
  )

  const { tasks: allTasks, hasLoaded } = useTasksList({
    assignedTo: userId,
    statuses: activeStatuses,
    enabled: isOpen,
  })

  const isLoading = !hasLoaded

  // "Overdue" = due before today (see getDueStatus). due_date_end is inclusive
  // on the backend, so bound it at yesterday to exclude tasks due today.
  const overdueDueDateEnd = useMemo(() => {
    const d = new Date()
    d.setDate(d.getDate() - 1)
    return d
  }, [])

  // Separate bounded query so the badge count stays eager (loads on every
  // route) without pulling the full unbounded task list until the dropdown opens.
  const { totalCount: overdueCount } = useTasksList({
    assignedTo: userId,
    statuses: activeStatuses,
    dueDateEnd: overdueDueDateEnd,
    limit: 1,
  })

  // Sort by due date (earliest first, no-due-date last)
  const sortedTasks = useMemo(() => {
    const sorted = [...allTasks].sort((a, b) => {
      if (!a.dueDate && !b.dueDate) return 0
      if (!a.dueDate) return 1
      if (!b.dueDate) return -1
      return Number(a.dueDate.seconds) - Number(b.dueDate.seconds)
    })
    return sorted
  }, [allTasks])

  // Filter by tab
  const filteredTasks = useMemo(() => {
    if (tab === 'today') {
      return sortedTasks.filter((t) => {
        const status = getDueStatus(t)
        return status === 'overdue' || status === 'today'
      })
    }
    return sortedTasks
  }, [sortedTasks, tab])

  const dueGroups = useMemo(() => groupByDue(filteredTasks), [filteredTasks])

  const totalCount = sortedTasks.length

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleClick = useCallback(
    (task: TaskLite) => {
      if (task.parentType === ParentTypeEnum.PARENT_TYPE_UNKNOWN || !task.parentId) return
      const parent = getParentRoute(task.parentType, task.parentId)
      if (!parent) return
      setIsOpen(false)
      navigate({ to: parent.route })
    },
    [navigate, setIsOpen],
  )

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className={cn('relative', className)}
          aria-label={`Tasks${totalCount > 0 ? ` (${totalCount} active)` : ''}`}
        >
          <ListChecks className="h-5 w-5" />
          {overdueCount > 0 && (
            <Badge
              variant="destructive"
              className="absolute -top-1 -right-1 h-[18px] min-w-[18px] px-1 flex items-center justify-center text-[10px] font-bold rounded-full pointer-events-none animate-in zoom-in-50 duration-200"
>
              {overdueCount > 99 ? '99+' : overdueCount}
            </Badge>
          )}
          <span className="sr-only">Tasks</span>
        </Button>
      </PopoverTrigger>

      <PopoverContent side={side} align={align} className="w-[400px] p-0 overflow-hidden" sideOffset={8}>
        {/* ── Header ────────────────────────────────────────────────────── */}
        <div className="flex items-center justify-between gap-2 px-4 py-2.5 border-b">
          <div className="flex items-center gap-3">
            <h3 className="text-sm font-semibold">My Tasks</h3>
            <div className="flex items-center p-0.5 rounded-md bg-muted">
              <button
                onClick={() => setTab('all')}
                className={cn(
                  'px-2 py-0.5 text-[11px] font-medium rounded-[5px] transition-all duration-150',
                  tab === 'all'
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                All{totalCount > 0 && ` (${totalCount})`}
              </button>
              <button
                onClick={() => setTab('today')}
                className={cn(
                  'px-2 py-0.5 text-[11px] font-medium rounded-[5px] transition-all duration-150',
                  tab === 'today'
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                Urgent
              </button>
            </div>
          </div>
        </div>

        {/* ── Content ───────────────────────────────────────────────────── */}
        <Skeleton
          data={isLoading ? null : dueGroups}
          sample={SAMPLE_DUE_GROUPS}
          label="Loading tasks"
          empty={!isLoading && filteredTasks.length === 0}
          emptyMessage={<EmptyTasks tab={tab} />}
        >
          {(dueGroups) => (
            <div className="max-h-[420px] overflow-y-auto">
              <div className="py-1">
                {dueGroups.map((group, gi) => (
                  <div key={group.label}>
                    <div className="px-4 pt-2.5 pb-1.5">
                      <span
                        className={cn(
                          'text-[10px] font-semibold uppercase tracking-widest',
                          groupLabelStyles[group.label] || 'text-muted-foreground/60',
                        )}
                      >
                        {group.label}
                      </span>
                    </div>
                    {group.items.map((task, i) => (
                      <TaskFeedItem
                        key={task.id}
                        task={task}
                        index={gi * 10 + i}
                        onClick={() => handleClick(task)}
                      />
                    ))}
                  </div>
                ))}
              </div>
            </div>
          )}
        </Skeleton>
      </PopoverContent>
    </Popover>
  )
}

// ─── Task item ───────────────────────────────────────────────────────────────

interface TaskFeedItemProps {
  task: TaskLite
  index: number
  onClick: () => void
}

function TaskFeedItem({ task, index, onClick }: TaskFeedItemProps) {
  const { updateTaskStatus, isLoading: isUpdating } = useUpdateTask()
  const dueStatus = getDueStatus(task)
  const style = dueStyles[dueStatus]
  const isInProgress = task.taskStatus === TaskStatusEnum.TASK_STATUS_IN_PROGRESS

  const dueDateOnly = timestampToDateOnly(task.dueDate)
  const dueDateStr = dueDateOnly ? formatShortDate(dueDateOnly) : null

  const parent = task.parentType !== ParentTypeEnum.PARENT_TYPE_UNKNOWN && task.parentId
    ? getParentRoute(task.parentType, task.parentId)
    : null

  const handleComplete = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      updateTaskStatus(task.id, TaskStatusEnum.TASK_STATUS_COMPLETED)
    },
    [updateTaskStatus, task.id],
  )

  const handleInProgress = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      if (isInProgress) {
        updateTaskStatus(task.id, TaskStatusEnum.TASK_STATUS_NOT_STARTED)
      } else {
        updateTaskStatus(task.id, TaskStatusEnum.TASK_STATUS_IN_PROGRESS)
      }
    },
    [updateTaskStatus, task.id, isInProgress],
  )

  return (
    <button
      onClick={onClick}
      className={cn(
        'w-full text-left px-4 py-3 transition-all duration-150',
        'hover:bg-muted/50 focus-visible:bg-muted/50 focus-visible:outline-none',
        'group cursor-pointer relative',
        'border-l-2',
        'animate-in fade-in-0 slide-in-from-bottom-1 duration-200',
        style.border,
        dueStatus === 'overdue' && 'bg-red-500/[0.04]',
        dueStatus === 'today' && 'bg-amber-500/[0.04]',
      )}
      style={{ animationDelay: `${Math.min(index * 30, 150)}ms`, animationFillMode: 'backwards' }}
    >
      <div className="flex gap-3 items-start">
        {/* Status checkbox */}
        <div className="flex-shrink-0 pt-0.5">
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                role="button"
                tabIndex={0}
                onClick={handleComplete}
                className={cn(
                  'flex items-center justify-center w-5 h-5 rounded-full transition-colors duration-100',
                  'hover:text-emerald-600 hover:bg-emerald-500/10',
                  isUpdating && 'opacity-50 pointer-events-none',
                  dueStatus === 'overdue'
                    ? 'text-red-400'
                    : dueStatus === 'today'
                      ? 'text-amber-400'
                      : 'text-muted-foreground/40',
                )}
              >
                {isUpdating ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Circle className="h-4 w-4" />
                )}
              </span>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="text-xs">
              Mark as complete
            </TooltipContent>
          </Tooltip>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0 pr-8">
          <p
            className={cn(
              'text-[13px] leading-snug font-medium',
              dueStatus === 'overdue' ? 'text-foreground' : 'text-foreground',
            )}
          >
            {task.title}
          </p>

          {/* Meta row */}
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            {/* Due date badge */}
            {dueDateStr && (
              <span
                className={cn(
                  'inline-flex items-center gap-1 text-[11px] font-medium px-1.5 py-0.5 rounded-md',
                  style.badge,
                )}
              >
                {dueStatus === 'overdue' && <AlertTriangle className="h-3 w-3" />}
                {dueStatus === 'today' && <Calendar className="h-3 w-3" />}
                {dueDateStr}
              </span>
            )}

            {/* In progress indicator */}
            {isInProgress && (
              <span className="inline-flex items-center gap-1 text-[11px] font-medium text-blue-600 dark:text-blue-400">
                <Loader2 className="h-3 w-3" />
                In progress
              </span>
            )}

            {/* Parent link */}
            {parent && (
              <span className="text-[11px] text-muted-foreground/50 truncate max-w-[160px]">
                {parent.label}{task.parentName ? `: ${task.parentName}` : ''}
              </span>
            )}
          </div>
        </div>

        {/* Hover actions — top-right corner */}
        <div className="absolute top-2 right-3 flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity duration-150 z-10">
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                role="button"
                tabIndex={0}
                onClick={handleInProgress}
                className={cn(
                  'h-6 w-6 rounded-md inline-flex items-center justify-center transition-colors duration-100',
                  isInProgress
                    ? 'text-blue-600 bg-blue-500/10 hover:bg-blue-500/20'
                    : 'text-muted-foreground/60 hover:text-blue-600 hover:bg-blue-500/10',
                )}
              >
                <Loader2 className="h-3.5 w-3.5" />
              </span>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="text-xs">
              {isInProgress ? 'Remove in progress' : 'Set in progress'}
            </TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                role="button"
                tabIndex={0}
                onClick={handleComplete}
                className="h-6 w-6 rounded-md inline-flex items-center justify-center text-muted-foreground/60 hover:text-emerald-600 hover:bg-emerald-500/10 transition-colors duration-100"
              >
                <CheckCircle2 className="h-3.5 w-3.5" />
              </span>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="text-xs">
              Mark as complete
            </TooltipContent>
          </Tooltip>
        </div>

        {/* Hover chevron */}
        {parent && (
          <div className="flex-shrink-0 self-center opacity-0 group-hover:opacity-60 transition-all duration-150">
            <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
          </div>
        )}
      </div>
    </button>
  )
}

// ─── Empty state ─────────────────────────────────────────────────────────────

function EmptyTasks({ tab }: { tab: TabFilter }) {
  return (
    <div className="flex flex-col items-center justify-center py-14 px-6">
      <div className="w-12 h-12 rounded-2xl bg-muted/80 flex items-center justify-center mb-4">
        <Inbox className="h-6 w-6 text-muted-foreground/50" />
      </div>
      <p className="text-sm font-medium text-foreground mb-1">
        {tab === 'today' ? 'Nothing urgent' : 'All done'}
      </p>
      <p className="text-xs text-muted-foreground/70 text-center leading-relaxed max-w-[220px]">
        {tab === 'today'
          ? 'No overdue or due-today tasks. Nice work.'
          : 'You have no active tasks assigned to you.'}
      </p>
    </div>
  )
}
